import { Translation } from '../../../models/Translations';

// ── Cost source (Real vs Standard) ───────────────────────────────────────────
/**
 * Marks where an <c>AreaCostEntry</c>'s costs came from. Real entries are
 * built from actual period data (IT lines, employee assignments, utility
 * invoices). Standard entries are synthesized from a
 * <c>FabricColorStandardCost</c> row when a WIP roll has not physically
 * processed an area at month-end — the engine fills the gap so reports show a
 * consistent total per roll.
 *
 * Numeric values mirror the C# enum on the wire (System.Text.Json default).
 */
export enum CostSource {
  Real = 0,
  Standard = 1,
}

export const COST_SOURCE_LABELS: Readonly<Record<CostSource, string>> = {
  [CostSource.Real]: 'Real',
  [CostSource.Standard]: 'Standard',
};

/**
 * Filter selection on the cost-analysis page. Two values only:
 *  - <c>'Real'</c>: every roll is kept (so the breadth of rolls in view
 *    matches Estimated), but every Standard contribution is stripped from
 *    the dataset before aggregation — Standard whole-entries are dropped,
 *    Standard-tagged keys inside mixed Real entries are removed, and rolls
 *    left with no Real contribution at all are excluded. Totals therefore
 *    show the Real cost portion only.
 *  - <c>'Estimated'</c>: every roll included, with Standard imputations
 *    counted toward totals — the dashboard's default and the closest
 *    equivalent to the legacy "Both" option.
 *  - <c>'Standard'</c>: same breadth and aggregation as Estimated, but every
 *    cost is resolved to its catalog standard — real costs are replaced by the
 *    standard equivalent. In the By Process table the displayed pounds stay
 *    real; only the cost numerator switches to standard.
 */
export type CostSourceFilter = 'Real' | 'Estimated' | 'Standard';

// ── Production Card (Production.API) ─────────────────────────────────────────
/**
 * Production.API <c>ProductionCard.Status</c> enum. Numeric wire values mirror
 * the C# definition. Re-declared locally because Finance.API's
 * <c>/api/v1/enums</c> endpoint only exposes <c>Sierra.Finance.Models</c>
 * enums; <c>ProductionCardStatus</c> lives in <c>Sierra.Production.Models</c>.
 */
export enum ProductionCardStatus {
  Draft = 0,
  Submitted = 1,
  Approved = 2,
  PlanningForBatching = 3,
  InRouteToBatching = 4,
  Batching = 5,
  InRouteToBrd = 6,
  Brd = 7,
  Deleted = 8,
  Rejected = 9,
  Dyeing = 10,
  ReadyForFinishing = 11,
  Finishing = 12,
  FinishedGood = 13,
  Shipped = 14,
  Closed = 15,
  Reassignment = 16,
}

export function getProductionCardStatusLabel(status: ProductionCardStatus): Translation {
  const labels: Record<ProductionCardStatus, Translation> = {
    [ProductionCardStatus.Draft]: { es: 'Borrador', en: 'Draft' },
    [ProductionCardStatus.Submitted]: { es: 'Enviada', en: 'Submitted' },
    [ProductionCardStatus.Approved]: { es: 'Aprobada', en: 'Approved' },
    [ProductionCardStatus.PlanningForBatching]: { es: 'Planificación Batching', en: 'Planning for Batching' },
    [ProductionCardStatus.InRouteToBatching]: { es: 'En Ruta a Batching', en: 'In Route to Batching' },
    [ProductionCardStatus.Batching]: { es: 'Batching', en: 'Batching' },
    [ProductionCardStatus.InRouteToBrd]: { es: 'En Ruta a BRD', en: 'In Route to BRD' },
    [ProductionCardStatus.Brd]: { es: 'BRD', en: 'BRD' },
    [ProductionCardStatus.Deleted]: { es: 'Eliminada', en: 'Deleted' },
    [ProductionCardStatus.Rejected]: { es: 'Rechazada', en: 'Rejected' },
    [ProductionCardStatus.Dyeing]: { es: 'Teñido', en: 'Dyeing' },
    [ProductionCardStatus.ReadyForFinishing]: { es: 'Listo para Acabado', en: 'Ready for Finishing' },
    [ProductionCardStatus.Finishing]: { es: 'Acabado', en: 'Finishing' },
    [ProductionCardStatus.FinishedGood]: { es: 'Producto Terminado', en: 'Finished Good' },
    [ProductionCardStatus.Shipped]: { es: 'Enviado', en: 'Shipped' },
    [ProductionCardStatus.Closed]: { es: 'Cerrada', en: 'Closed' },
    [ProductionCardStatus.Reassignment]: { es: 'Reasignación', en: 'Reassignment' },
  };
  return labels[status] || { es: 'Desconocido', en: 'Unknown' };
}

// ── Work Order (Order.API) ───────────────────────────────────────────────────
/**
 * Order.API <c>WorkOrder.OrderType</c> enum. Mirrors the C# definition order
 * so the numeric wire values match without translation.
 */
export enum OrderType {
  Sample = 0,
  Bulk = 1,
  Greige = 2,
  ShortFall = 3,
  Linked = 4,
  BGrade = 5,
}

export function getOrderTypeLabel(type: OrderType): Translation {
  const labels: Record<OrderType, Translation> = {
    [OrderType.Sample]: { es: 'Muestra', en: 'Sample' },
    [OrderType.Bulk]: { es: 'Bulk', en: 'Bulk' },
    [OrderType.Greige]: { es: 'Crudo', en: 'Greige' },
    [OrderType.ShortFall]: { es: 'Faltante', en: 'Short Fall' },
    [OrderType.Linked]: { es: 'Vinculada', en: 'Linked' },
    [OrderType.BGrade]: { es: 'B Grade', en: 'B Grade' },
  };
  return labels[type] || { es: 'Desconocido', en: 'Unknown' };
}

/**
 * @deprecated Use {@link getOrderTypeLabel} with the active language instead.
 * Kept as a thin English-only alias for legacy dropdown wiring; will be removed
 * once all consumers route through the bilingual helper.
 */
export const ORDER_TYPE_LABELS: Readonly<Record<OrderType, string>> = {
  [OrderType.Sample]: getOrderTypeLabel(OrderType.Sample).en,
  [OrderType.Bulk]: getOrderTypeLabel(OrderType.Bulk).en,
  [OrderType.Greige]: getOrderTypeLabel(OrderType.Greige).en,
  [OrderType.ShortFall]: getOrderTypeLabel(OrderType.ShortFall).en,
  [OrderType.Linked]: getOrderTypeLabel(OrderType.Linked).en,
  [OrderType.BGrade]: getOrderTypeLabel(OrderType.BGrade).en,
};

/**
 * Minimal projection of a Work Order used by Cost Analysis. Populated once per
 * page load via batch lookup; cached in <c>CostAnalysisStateService</c> so the
 * filter + the WO chip in the fabric-summary tree read from the same source.
 */
export interface WorkOrderSummary {
  /** Same value as <c>RollMonthlyCostDto.workOrderKey.reference</c>. */
  reference: string;
  number: string;
  type: OrderType | null;
  customerReference: string | null;
  customerName: string | null;
}

// ── Fabric display resolution ────────────────────────────────────────────────
/**
 * Pick the human-friendly label for a Fabric. Costing UI must show the
 * commercial description first (it is the field the business cares about);
 * `longDescription` is only used when `commercialFabricDescription` is null,
 * empty, or whitespace.
 *
 * Returns `undefined` when neither field has content — callers then fall back
 * to the fabric code so a fabric never renders as a blank cell.
 */
export interface FabricDescriptionFields {
  commercialFabricDescription?: string;
  longDescription?: string;
}

export function resolveFabricDescription(fabric: FabricDescriptionFields | undefined): string | undefined {
  if (!fabric) {
    return undefined;
  }
  const commercial = fabric.commercialFabricDescription;
  if (commercial && commercial.trim().length > 0) {
    return commercial.trim();
  }
  const long = fabric.longDescription;
  if (long && long.trim().length > 0) {
    return long.trim();
  }
  return undefined;
}
